import { useState } from "react";
import AuthorizedLayout from "@/layouts/AuthorizedLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Nurse } from "@/lib/types/models.ts";
import CreateNurseDialog from "@/components/dialogs/CreateNurseDialog";
import EditNurseDialog from "@/components/dialogs/EditNurseDialog";
import { usePage } from "@inertiajs/react";
import { router } from "@inertiajs/core";
import { NurseCreatePayload, NurseUpdatePayload } from "@/lib/types/payloads.ts";
import { Plus, Trash, Edit } from "lucide-react";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.tsx";
import { PageTitle } from "@/components/PageTitle.tsx";

export default function NursesIndex() {
    const { nurses = [], translations: t } = usePage().props as any;

    const [editingNurse, setEditingNurse] = useState<Nurse | null>(null);
    const [deleteNurse, setDeleteNurse] = useState<Nurse | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const handleCreate = async (data: NurseCreatePayload) => {
        router.post("/nurses", { nurse: data as Record<string, any> });
        setIsCreateDialogOpen(false);
    };

    const handleUpdate = async (id: string, data: NurseUpdatePayload) => {
        router.patch(`/nurses/${id}`, { nurse: data as Record<string, any> });
        setEditingNurse(null);
    };

    const handleDelete = () => {
        if (!deleteNurse) return;
        router.delete(`/nurses/${deleteNurse.id}`);
        setDeleteNurse(null);
    };

    return (
        <AuthorizedLayout>
            <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <PageTitle>{t.title}</PageTitle>
                    <Button size="icon" onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus />
                    </Button>
                </div>

                {nurses.length === 0 ? (
                    <p className="text-muted-foreground">{t.no_nurses_found}</p>
                ) : (
                    <>
                        <div className="hidden md:block">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t.headers.name}</TableHead>
                                            <TableHead>{t.headers.username}</TableHead>
                                            <TableHead>{t.headers.phone}</TableHead>
                                            <TableHead>{t.headers.actions}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {nurses.map((nurse: Nurse, index: number) => (
                                            <TableRow key={nurse.id} className="hover:bg-muted/50">
                                                <TableCell>{nurse.name}</TableCell>
                                                <TableCell>{nurse.username}</TableCell>
                                                <TableCell>{nurse.phone_number ?? "-"}</TableCell>
                                                <TableCell className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => setEditingNurse(nurse)}
                                                    >
                                                        <Edit />
                                                    </Button>
                                                    {index >= 1 && (
                                                        <Button
                                                            variant="destructive"
                                                            size="icon"
                                                            onClick={() => setDeleteNurse(nurse)}
                                                        >
                                                            <Trash />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        <div className="md:hidden space-y-3">
                            {nurses.map((nurse: Nurse, index: number) => (
                                <div
                                    key={nurse.id}
                                    className="bg-white border rounded-lg p-3 shadow-sm flex flex-col gap-2"
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="text-base font-medium">{nurse.name}</div>
                                            <div className="text-sm text-muted-foreground">{nurse.username}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setEditingNurse(nurse)}
                                            >
                                                <Edit />
                                            </Button>
                                            {index >= 1 && (
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    onClick={() => setDeleteNurse(nurse)}
                                                >
                                                    <Trash />
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{t.headers.phone}:</span>
                                            <span>{nurse.phone_number ?? "-"}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <CreateNurseDialog
                    isOpen={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onCreate={handleCreate}
                />
                {editingNurse && (
                    <EditNurseDialog
                        isOpen={true}
                        onOpenChange={() => setEditingNurse(null)}
                        onUpdate={(data) => handleUpdate(editingNurse.id, data)}
                        nurse={editingNurse}
                    />
                )}
                <ConfirmDialog
                    isOpen={!!deleteNurse}
                    onOpenChange={() => setDeleteNurse(null)}
                    title={t.delete_dialog.title}
                    description={t.delete_dialog.description}
                    confirmText={t.delete_dialog.confirm}
                    cancelText={t.delete_dialog.cancel}
                    onConfirm={() => handleDelete()}
                />
            </div>
        </AuthorizedLayout>
    );
}